import type { TreeData, TreeItem, TreeItemContext } from '../types';

/**
 * Builds an anchor ID from an array of elements, filtering out null/undefined values.
 * @param elements - Array of strings, numbers, or null/undefined values
 * @returns The anchor ID string (e.g., "workExperience-42")
 */
export function buildAnchorLink(
  elements: (string | number | null | undefined)[] = [],
): string {
  return elements.filter(Boolean).join('-');
}

/**
 * Builds an anchor ID from a TreeItemContext.
 * @param context - The context object containing hrefBase and id
 * @returns The anchor ID string
 */
export function buildAnchorLinkFromContext(context?: TreeItemContext): string {
  if (!context) {
    return '';
  }

  return buildAnchorLink([context.hrefBase, context.id]);
}

/**
 * Extracts all anchor IDs from a tree data structure.
 * @param treeData - The tree data to extract IDs from
 * @returns Array of anchor ID selectors (e.g., ["#workExperience", "#workExperience-42"])
 */
export function extractAnchorIDsFromTree(treeData: TreeData): string[] {
  const elements: string[] = [];

  function extract(treeItem: TreeItem) {
    if (treeItem.context) {
      const anchorId = buildAnchorLinkFromContext(treeItem.context);
      if (anchorId) {
        elements.push(`#${anchorId}`);
      }
    }
    treeItem.children?.forEach(extract);
  }

  treeData.forEach(extract);

  return elements;
}
